import React, { Suspense, useCallback, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { Progress } from '@backstage/core-components';
import 'swagger-ui-react/swagger-ui.css';
import { parseApiDefinition } from './apiDefinition';
import { exportPostmanCollection } from './postmanExport';
import { blockSwaggerRequest, createPostmanPlugin } from './postmanPlugin';

const SwaggerUI = React.lazy(() => import('swagger-ui-react'));
const useStyles = makeStyles(theme => ({
  root: {
    minWidth: 0,
    '& .swagger-ui': {
      fontFamily: theme.typography.fontFamily,
      color: theme.palette.text.primary,
    },
    '& .swagger-ui .info .title, & .swagger-ui .opblock-tag, & .swagger-ui .opblock-summary-path, & .swagger-ui .opblock-summary-description, & .swagger-ui .opblock-description-wrapper, & .swagger-ui .parameter__name, & .swagger-ui .parameter__type, & .swagger-ui .response-col_status, & .swagger-ui .response-col_description, & .swagger-ui .model-title, & .swagger-ui .model, & .swagger-ui h4, & .swagger-ui h5, & .swagger-ui label, & .swagger-ui th, & .swagger-ui td, & .swagger-ui p, & .swagger-ui li':
      {
        fontFamily: theme.typography.fontFamily,
        color: theme.palette.text.primary,
      },
    '& .swagger-ui .scheme-container, & .swagger-ui .opblock-section-header, & .swagger-ui .model-box, & .swagger-ui section.models .model-container':
      {
        background: theme.palette.background.paper,
        boxShadow: 'none',
      },
    '& .swagger-ui section.models, & .swagger-ui .opblock-tag, & .swagger-ui th, & .swagger-ui td':
      {
        borderColor: theme.palette.divider,
      },
    '& .swagger-ui .btn, & .swagger-ui input, & .swagger-ui select, & .swagger-ui textarea':
      {
        borderRadius: theme.shape.borderRadius,
        fontSize: theme.typography.body2.fontSize,
        fontFamily: theme.typography.fontFamily,
        color: theme.palette.text.primary,
      },
    '& .swagger-ui input, & .swagger-ui select, & .swagger-ui textarea': {
      background: theme.palette.background.paper,
    },
    '& .swagger-ui .btn.execute': {
      background: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
    '& .swagger-ui .opblock-summary-method': {
      minWidth: '8ch',
      flexShrink: 0,
    },
    '& .swagger-ui .opblock-summary-path': {
      minWidth: 0,
      overflowWrap: 'anywhere',
    },
    '& .swagger-ui .responses-wrapper, & .swagger-ui .model-box': {
      overflowX: 'auto',
    },
    '& .swagger-ui .dialog-ux .modal-ux, & .swagger-ui .dialog-ux .modal-ux-header, & .swagger-ui .dialog-ux .modal-ux-content, & .swagger-ui .dialog-ux .auth-container':
      {
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
      },
    '& .swagger-ui .dialog-ux .modal-ux-header': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '& .swagger-ui .dialog-ux h3, & .swagger-ui .dialog-ux h4, & .swagger-ui .dialog-ux label, & .swagger-ui .dialog-ux p, & .swagger-ui .dialog-ux code, & .swagger-ui .dialog-ux .close-modal':
      {
        color: theme.palette.text.primary,
      },
    '& .swagger-ui .dialog-ux .btn.modal-btn': {
      color: theme.palette.text.primary,
      background: theme.palette.background.paper,
      borderColor: theme.palette.divider,
    },
    '& .swagger-ui .dialog-ux .btn.modal-btn.authorize': {
      background: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
  },
  notice: { marginBottom: theme.spacing(2) },
  toolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(2),
  },
  curl: {
    marginTop: theme.spacing(2),
    '& textarea': { fontFamily: theme.typography.fontFamily },
  },
}));

type ExportedRequest = {
  curl: string;
  operation: string;
  copied: boolean;
  placeholders: string[];
};

export function SwaggerPostmanDefinition({
  definition,
}: {
  definition: string;
}) {
  const classes = useStyles();
  const parsed = useMemo(() => parseApiDefinition(definition), [definition]);
  const [exported, setExported] = useState<ExportedRequest>();
  const [error, setError] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(false);
  const swaggerSystem = useRef<any>();
  const exportCollection = useCallback(async () => {
    setExporting(true);
    setExportError(false);
    try {
      await exportPostmanCollection(
        parsed.document as object,
        swaggerSystem.current?.oas3Selectors.selectedServer() || undefined,
      );
    } catch {
      setExportError(true);
    } finally {
      setExporting(false);
    }
  }, [parsed.document]);
  const copy = useCallback((curl: string) => {
    if (!navigator.clipboard?.writeText) return;
    navigator.clipboard
      .writeText(curl)
      .then(() => {
        setExported(previous =>
          previous?.curl === curl ? { ...previous, copied: true } : previous,
        );
      })
      .catch(() => {
        // The dialog remains available for manual copying when clipboard access is denied.
      });
  }, []);
  const onCopy = useCallback(
    (curl: string, operation: string, placeholders: string[]) => {
      setError(false);
      setExported({ curl, operation, copied: false, placeholders });
      copy(curl);
    },
    [copy],
  );
  const plugins = useMemo(
    () => [createPostmanPlugin(onCopy, () => setError(true))],
    [onCopy],
  );

  if (parsed.kind !== 'openapi') {
    return (
      <Alert severity="warning">
        {parsed.kind === 'empty'
          ? 'Esta API no tiene una definicion OpenAPI.'
          : 'No se pudo interpretar la definicion OpenAPI. Revisa la pestana Raw.'}
      </Alert>
    );
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.toolbar}>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          disabled={exporting}
          onClick={exportCollection}
        >
          Exportar colección Postman
        </Button>
      </Box>
      {exportError && (
        <Alert severity="error" className={classes.notice}>
          No se pudo exportar la colección Postman. Revisa la definición y el
          servidor seleccionado.
        </Alert>
      )}
      {error && (
        <Alert severity="error" className={classes.notice}>
          No se pudo generar el cURL. Revisa los parametros, el body y el
          servidor seleccionado.
        </Alert>
      )}
      <Suspense fallback={<Progress />}>
        <SwaggerUI
          spec={parsed.document}
          url=""
          plugins={plugins}
          tryItOutEnabled
          queryConfigEnabled={false}
          deepLinking={false}
          persistAuthorization={false}
          withCredentials={false}
          requestInterceptor={blockSwaggerRequest}
          onComplete={system => {
            swaggerSystem.current = system;
          }}
        />
      </Suspense>
      <Dialog
        open={Boolean(exported)}
        onClose={() => setExported(undefined)}
        fullWidth
        maxWidth="md"
        aria-labelledby="postman-copy-title"
      >
        <DialogTitle id="postman-copy-title">Copiar cURL</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2">{exported?.operation}</Typography>
          <Alert severity={exported?.copied ? 'success' : 'info'}>
            {exported?.copied
              ? 'cURL copiado. Ejecutalo en tu terminal o importalo en tu cliente de APIs.'
              : 'Copia el comando para usarlo en tu terminal o cliente de APIs.'}
          </Alert>
          {Boolean(exported?.placeholders?.length) && (
            <Alert severity="warning">
              Sustituye los marcadores REPLACE_* por valores validos antes de
              ejecutar el comando. Campos pendientes:{' '}
              {exported?.placeholders?.join(', ')}.
            </Alert>
          )}
          <TextField
            id="postman-curl"
            className={classes.curl}
            label="cURL"
            value={exported?.curl || ''}
            multiline
            minRows={6}
            maxRows={16}
            fullWidth
            variant="outlined"
            InputProps={{ readOnly: true }}
            onFocus={event => event.target.select()}
          />
          <Typography variant="caption" color="textSecondary">
            No se envio ninguna peticion al API. El comando puede contener
            credenciales; no lo compartas. Ejecutalo desde un equipo con acceso
            de red al servicio.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExported(undefined)}>Cerrar</Button>
          <Button
            color="primary"
            onClick={() => exported && copy(exported.curl)}
          >
            Copiar cURL
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
